<script setup lang="ts">
import type { INewsDataClientDTO } from '~/shared/api/client/news/news.dto';
import {
  resizeCropSlot,
  resizeImage,
  resizeImageAttrs,
} from '~/shared/lib/resizeImage';
import { CROP_RATIO_CHRONICLE } from '~/shared/lib/crop-ratio.const';
import {
  NEWS_ITEM_GALLERY_WIDE_WIDTH,
  NEWS_ITEM_GALLERY_PHOTO_WIDTH,
  NEWS_ITEM_GALLERY_PHOTO_HEIGHT,
} from './lib/const';

const props = defineProps<INewsDataClientDTO>();

const panoAttrs = computed(() =>
  resizeCropSlot(
    props.image_one?.file_url ?? props.news.image?.photo.file_url,
    CROP_RATIO_CHRONICLE.gallery_wide,
    0,
    { width: NEWS_ITEM_GALLERY_WIDE_WIDTH },
  ),
);

/* Фото сетки приходят из галереи, кропа под них в конфиге нет. */
const galleryPhotoAttrs = (url: string | null | undefined) =>
  resizeImageAttrs(url, {
    width: NEWS_ITEM_GALLERY_PHOTO_WIDTH,
    height: NEWS_ITEM_GALLERY_PHOTO_HEIGHT,
    mode: 'fill',
  });

const isRedirect = computed(() =>
  Boolean(props.news.redirect && props.news.redirect.length > 0),
);

const link =
  isRedirect.value && props.news.redirect
    ? props.news.redirect
    : `/${props.news.type}/${props.news.id}`;

const isGalleryPaid = computed(
  () =>
    props.news.is_gallery &&
    props.news.is_paid &&
    !!props.news.logo?.photo.file_url,
);

const handleClick = () => {
  if (isRedirect.value) {
    navigateTo(link, {
      external: true,
      open: {
        target: '_blank',
      },
    });
  } else {
    navigateTo(link);
  }
};
</script>

<template>
  <div
    :class="[
      $style.item,
      {
        [$style.item_object!]: props.news.type === 'new_object',
      },
    ]"
    @click="handleClick"
  >
    <div :class="$style.item__header">
      <div :class="$style.item__headerTitle">{{ props.title }}</div>
      <Button
        :class="$style.item__headerButton"
        theme="primary"
        size="standard"
        :to="link"
      >
        Подробнее
      </Button>
    </div>
    <div
      v-if="props.news.type === 'new_object'"
      :class="[$style.item__gallery, $style.item__gallery_wide]"
    >
      <div :class="$style.item__galleryObject">
        <img
          :class="$style.item__galleryPano"
          :src="panoAttrs.src"
          :srcset="panoAttrs.srcset || undefined"
          :alt="props.title"
        />
      </div>
    </div>
    <div
      v-else-if="props.news.gallery?.photos"
      :class="[$style.item__gallery, $style.item__gallery_grey]"
    >
      <div
        v-for="(item, key) in props.news.gallery?.photos?.slice(0, 4)"
        :key="`newsItemGalleryWidePhoto${key}`"
        :class="$style.item__galleryItem"
      >
        <img
          :class="$style.item__galleryImage"
          :src="galleryPhotoAttrs(item.photo.file_url).src"
          :srcset="galleryPhotoAttrs(item.photo.file_url).srcset || undefined"
          :alt="item.description"
        />

        <div
          v-if="key === 0 && isGalleryPaid"
          :class="$style.item__galleryPartner"
          :style="{
            backgroundImage: `url(${resizeImage(props.news.logo?.photo.file_url, { width: 120 })})`,
          }"
        />
      </div>
    </div>
    <div :class="$style.item__footer">
      <Button
        :class="$style.item__footerButton"
        theme="primary"
        size="standard"
        :to="link"
      >
        Подробнее
      </Button>
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  cursor: pointer;

  &_object {
    border-left: 4px solid var(--color-red-1);
  }

  &__header {
    display: flex;
    flex-flow: row nowrap;
    align-items: center;
    padding: 11px var(--space-2-5x);
    justify-content: space-between;
    gap: 200px;

    &Title {
      @include text_h5;
      color: var(--color-black-1);
    }
  }

  &__gallery {
    display: flex;
    flex-flow: row nowrap;

    &_wide {
      @include mobileSliderWrapper;
    }

    &_grey {
      background-color: var(--color-gray-bg);
    }

    &Item {
      position: relative;
      display: flex;
      flex-flow: row nowrap;
      width: 270px;
      border-right: 1px solid var(--color-gray-line);
      padding: var(--space-2-5x);

      &:nth-child(4) {
        border-right: none;
      }
    }

    &Image {
      width: 100%;
      height: 140px;
      object-fit: cover;
    }

    &Partner {
      position: absolute;
      left: var(--space-2-5x);
      top: var(--space-2-5x);
      width: 60px;
      height: 60px;
      background-color: var(--color-red-1);
      background-size: 38px;
      background-position: center;
      background-repeat: no-repeat;
    }

    &Object {
      width: 100%;
      padding-bottom: var(--space-2-5x);
    }

    &Pano {
      width: 100%;
      min-width: 1041px;
      height: 148px;
      object-fit: cover;
      margin: 0 var(--space-2-5x);
    }
  }

  &__footer {
    display: none;
  }
}

@media (hover: hover) {
  .item__headerTitle,
  .item__galleryPano,
  .item__galleryItem {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 1379px) {
  .item {
    &__header {
      gap: var(--space-2x);
    }
  }
}

@media (max-width: 767px) {
  .item {
    &__gallery {
      @include mobileSliderWrapper;

      &Item {
        @include mobileSliderItem;
        width: 262px;
        padding: var(--space-2-5x);
      }
    }
  }
}

@media (max-width: 479px) {
  .item {
    &__header {
      padding: var(--space-2x);

      &Button {
        display: none;
      }
    }

    &__gallery {
      &Item {
        padding: var(--space-2x);
      }

      &Partner {
        left: var(--space-2x);
        top: var(--space-2x);
      }

      &Object {
        padding-bottom: 0;
      }
    }

    &__footer {
      display: block;
      padding: var(--space-2x);
    }
  }
}
</style>
